/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/*
 * The file is automatically generated by Serenity UI.
 *
 * ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
 *
 * Found an issue? Submit a task to the ClickUp board:
 * https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
 */
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, type HTMLAttributes, createContext, forwardRef } from 'react';
import {
  type ContextValue,
  DEFAULT_SLOT,
  GroupContext as GroupCoreContext,
  Provider,
  useContextProps,
} from 'react-aria-components';
import { breakpoints } from '../../constants.stylex';
import { HamburgerMenuStrokeIcon } from '../../icons/hamburger-menu-stroke/hamburger-menu-stroke.icon';
import { $ } from '../../tokens.stylex';
import { AvatarContext } from '../avatar/avatar.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { LinkContext } from '../link/link.component';
import { SearchFieldContext } from '../search-field/search-field.component';
import { LinkIconButtonContext } from '../link-icon-button/link-icon-button.component';

interface HorizontalNavigationProps extends Omit<HTMLAttributes<HTMLElement>, 'style' | 'className'> {
  /** Whether the navigation side is open by default (controlled). */
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  style?: stylex.StyleXStyles;
}

const HorizontalNavigationContext = createContext<ContextValue<HorizontalNavigationProps, HTMLDivElement>>({});

const HorizontalNavigation = forwardRef((props: HorizontalNavigationProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, HorizontalNavigationContext);

  const { style, children, isOpen, onOpenChange, ...coreProps } = props;

  const isSingleChild = Children.toArray(children).length === 1;

  return (
    <div ref={ref} {...stylex.props(styles.base, isSingleChild && styles.justifyCenter, style)} {...coreProps}>
      <Provider
        values={[
          [
            GroupCoreContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  role: 'presentation',
                },
                [HORIZONTAL_NAVIGATION_SLOTS.LEFT]: {
                  role: 'presentation',
                  ...stylex.props(groupStyles.base, groupStyles.left),
                },
                [HORIZONTAL_NAVIGATION_SLOTS.RIGHT]: {
                  role: 'presentation',
                  ...stylex.props(groupStyles.base, groupStyles.right),
                },
                [HORIZONTAL_NAVIGATION_SLOTS.CENTER]: {
                  role: 'presentation',
                  ...stylex.props(groupStyles.base, groupStyles.center),
                },
              },
            },
          ],
          [
            SearchFieldContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  isRounded: true,
                  size: 44,
                  style: searchFieldStyles.base,
                },
                [HORIZONTAL_NAVIGATION_SLOTS.CENTER]: {
                  isRounded: true,
                  size: 44,
                  style: [groupStyles.center, searchFieldStyles.long],
                },
              },
            },
          ],
          [
            IconButtonContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 40,
                  variant: 'ghost',
                },
                [HORIZONTAL_NAVIGATION_SLOTS.HAMBURGER_BUTTON]: {
                  size: 40,
                  variant: 'ghost',
                  'aria-label': 'Open navigation panel',
                  onPress: () => onOpenChange?.(true),
                  children: <HamburgerMenuStrokeIcon />,
                },
              },
            },
          ],
          [
            LinkIconButtonContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 40,
                  variant: 'ghost',
                },
                [HORIZONTAL_NAVIGATION_SLOTS.HAMBURGER_BUTTON]: {
                  size: 40,
                  variant: 'ghost',
                  'aria-label': 'Open navigation panel',
                  onPress: () => onOpenChange?.(true),
                  children: <HamburgerMenuStrokeIcon />,
                },
              },
            }
          ],
          [AvatarContext, { size: 32 }],
          [
            LinkContext,
            {
              style: linkStyles.base,
              UNSAFE_IconContext: (renderProps) => ({
                style: iconStyles.base,
                color: (renderProps.isCurrent && 'primary') || (renderProps.isHovered && 'secondary') || 'quaternary',
              }),
            },
          ],
        ]}
      >
        {children}
      </Provider>
    </div>
  );
});

const styles = stylex.create({
  base: {
    paddingBlock: {
      default: 8,
      [breakpoints.medium]: 12,
      [breakpoints.small]: 12,
    },
    paddingLeft: 8,
    paddingRight: 16,
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: {
      default: 60,
      [breakpoints.medium]: 56,
      [breakpoints.small]: 56,
    },
    width: '100%',
    zIndex: 3,
  },
  justifyCenter: {
    justifyContent: 'center',
  },
});

const groupStyles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  left: {
    gap: 12,
  },
  right: {
    gap: 8,
  },
  center: {
    position: 'absolute',
    transform: 'translateX(-50%)',
    left: '50%',
  },
});

const linkStyles = stylex.create({
  base: {
    transition: 'color 0.2s ease-in-out',
    color: {
      default: $.textQuaternary,
      ':is([data-current])': $.textPrimary,
      ':is([data-hovered])': $.textSecondary,
    },
    marginRight: 24,
  },
});

const searchFieldStyles = stylex.create({
  base: { width: 336 },
  long: { width: 446 },
});

const iconStyles = stylex.create({
  base: {
    transition: 'fill, stroke, 0.2s ease-in-out',
  },
});

const HORIZONTAL_NAVIGATION_SLOTS = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
  HAMBURGER_BUTTON: 'hamburgerButton',
};

export { HORIZONTAL_NAVIGATION_SLOTS, HorizontalNavigation, HorizontalNavigationContext, type HorizontalNavigationProps };
